// ============================================================================
// SOURCE TB REGISTRY MODAL  (FSA-2.1.6)
// ============================================================================
// XL modal listing every SOURCE TB for an engagement so the auditor can:
//   • See what feeds the consolidated TB (name / version / upload date /
//     status).
//   • Toggle "Show history" to reveal superseded versions.
//   • Remove a specific source from consolidation ("Remove" row action).
//
// Opens from the "Source TBs (N)" link on the General/Basic tab.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { fsaTrialBalanceService } from 'api/services/fsa/fsaTrialBalanceService';
import type { ID } from 'api/types';
import type { SourceTbRegistryItem } from 'api/types/fsa/trialBalance.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  canEdit: boolean;
  onClose: () => void;
  /** Called after a successful Remove-from-consolidation. Parent refetches consolidated. */
  onChanged?: () => void;
}

const SourceTbRegistryModal: React.FC<Props> = ({ isOpen, engagementId, canEdit, onClose, onChanged }) => {
  const [items, setItems] = useState<SourceTbRegistryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<SourceTbRegistryItem | null>(null);
  const [removing, setRemoving] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fsaTrialBalanceService.listSources(engagementId);
      setItems(r.data ?? []);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load source TBs.');
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    if (isOpen) refetch();
  }, [isOpen, refetch]);

  const visible = showHistory ? items : items.filter(i => !i.isSuperseded);

  const handleConfirmRemove = useCallback(async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await fsaTrialBalanceService.removeSource(confirmRemove.id);
      notificationService.toast.success(`Removed '${confirmRemove.name} v${confirmRemove.version}' from consolidation.`);
      setConfirmRemove(null);
      await refetch();
      onChanged?.();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to remove source.');
    } finally {
      setRemoving(false);
    }
  }, [confirmRemove, refetch, onChanged]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Source TBs</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div style={toolbarStyle}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              checked={showHistory}
              onChange={e => setShowHistory(e.target.checked)}
            />
            Show history (superseded versions)
          </label>
          {/* "Add new TB for consolidation" intentionally removed from this
              modal — stacking the upload modal on top of the registry modal
              caused close-button / X-icon dismissal to fail (focus + z-index
              issues at the modal-on-modal boundary). Users add new TBs from
              the Trial Balance card on the General/Basic tab, where the
              upload modal opens as the top-level dialog. */}
        </div>

        <div className="modal-content" style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
          ) : visible.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              {items.length === 0
                ? 'No source TBs uploaded yet.'
                : 'No active sources. Toggle "Show history" to see superseded versions.'}
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Purpose</th>
                  <th style={thStyle}>Version</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Ledgers</th>
                  <th style={thStyle}>Uploaded</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {visible.map(item => (
                  <tr key={String(item.id)}>
                    <td style={tdStyle}><strong>{item.name}</strong></td>
                    <td style={tdStyle}><PurposeChip purpose={item.purpose} /></td>
                    <td style={tdStyle}>v{item.version}</td>
                    <td style={tdStyle}>{item.source}</td>
                    <td style={tdStyle}>{item.entryCount}</td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '0.8rem' }}>{item.uploadedByName ?? '—'}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{formatDateTime(item.uploadedAt)}</div>
                    </td>
                    <td style={tdStyle}>
                      <StatusChip item={item} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {canEdit && !item.isSuperseded && !item.isRemovedFromConsolidation && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setConfirmRemove(item)}
                          title="Remove from consolidation"
                        >
                          <IconSystem.trash size={12} /> Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove != null}
        title="Remove from consolidation?"
        message={confirmRemove
          ? `Remove '${confirmRemove.name} v${confirmRemove.version}' from the consolidated TB? The source row is kept for audit but no longer contributes to classifications or financial-statement rollups. The consolidated TB will be rebuilt.`
          : ''}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={removing}
        loadingMessage="Removing…"
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </>
  );
};

const PurposeChip: React.FC<{ purpose: 'CURRENT' | 'OPENING' }> = ({ purpose }) => {
  if (purpose === 'OPENING') {
    return (
      <span
        style={{ ...chipStyle, background: '#fef3c7', color: '#92400e' }}
        title="Prior year's full pre-closing TB (all ledgers including P&L). Feeds PY columns for comparatives on BS / PL / Notes. Sourced either from Roll Forward (auto-seeded from prior engagement) or via an explicit Prior Year TB upload."
      >
        📅 Prior Year
      </span>
    );
  }
  return (
    <span
      style={{ ...chipStyle, background: '#dbeafe', color: '#1e40af' }}
      title="Current-year working TB — closings feed CY columns on BS / PL / Notes."
    >
      📊 Current
    </span>
  );
};

const StatusChip: React.FC<{ item: SourceTbRegistryItem }> = ({ item }) => {
  if (item.isRemovedFromConsolidation) {
    return <span style={{ ...chipStyle, background: '#f1f5f9', color: '#475569' }}>Removed</span>;
  }
  if (item.isSuperseded) {
    return <span style={{ ...chipStyle, background: '#fef3c7', color: '#92400e' }}>Superseded</span>;
  }
  return <span style={{ ...chipStyle, background: '#dcfce7', color: '#166534' }}>Active</span>;
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 1rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.85rem',
};
const chipStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.7rem',
  fontWeight: 600,
};

export default SourceTbRegistryModal;
